import React, { useState, useMemo } from 'react';
import { BaseDrawer } from '../Drawer/BaseDrawer';
import { DrawerClose, DrawerContent, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { X, Search, Lightbulb, Camera, Droplet, Activity, FileText, Gauge, CheckSquare } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Highlight } from '../Highlight/Highlight';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

interface ObservationItem {
  text: string;
  source_type: string;
  health_status: string;
  resource_id: string | null;
}

interface FaultGroup {
  fault_type: string;
  health_status: string;
  diagnostic_id: string;
  observations: ObservationItem[];
}

export interface VisibilityConfig {
  observations: boolean;
  actions: boolean;
}

interface ObservationsDrawerProps {
  componentName: string;
  diagnostics: any[];
  evidencesText?: string[];
  actionsDescriptions?: string[];
  componentHealthStatus?: string;
  children: React.ReactNode;
  onOpenChange?: (open: boolean) => void;
  initialTab?: 'observations' | 'actions';
  visibilityConfig?: VisibilityConfig;
  isAssetOverview?: boolean; // True when showing all components
}

// Health status color mapping
const healthStatusColors: Record<string, string> = {
  critical: 'bg-critical text-critical-foreground',
  alarm: 'bg-alarm text-alarm-foreground',
  monitor: 'bg-monitor text-monitor-foreground',
  healthy: 'bg-healthy text-healthy-foreground',
  na: 'bg-muted text-muted-foreground',
};

// Health status border color mapping
const healthStatusBorderColors: Record<string, string> = {
  critical: 'border-l-critical',
  alarm: 'border-l-alarm',
  monitor: 'border-l-monitor',
  healthy: 'border-l-healthy',
  na: 'border-l-muted',
};

// Source type icon mapping
const getSourceIcon = (sourceType: string) => {
  const type = sourceType?.toLowerCase() || '';
  if (type.includes('camera')) return <Camera className="h-4 w-4" />;
  if (type.includes('oil')) return <Droplet className="h-4 w-4" />;
  if (type.includes('vibration')) return <Activity className="h-4 w-4" />;
  if (type.includes('report')) return <FileText className="h-4 w-4" />;
  if (type.includes('sensor')) return <Gauge className="h-4 w-4" />;
  return <Lightbulb className="h-4 w-4" />;
};

export const ObservationsDrawer = ({
  componentName,
  diagnostics,
  evidencesText,
  actionsDescriptions,
  componentHealthStatus,
  children,
  onOpenChange,
  initialTab = 'observations',
  visibilityConfig = { observations: true, actions: true },
  isAssetOverview = false,
}: ObservationsDrawerProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState(initialTab);

  // Transform diagnostics into fault groups with observations
  const faultGroups = useMemo(() => {
    // If Asset Overview mode with diagnostics, prioritize diagnostics to preserve component context
    if (isAssetOverview && diagnostics && diagnostics.length > 0) {
      // Group diagnostics by component
      const componentGroups = diagnostics.reduce((acc: any, diagnostic: any) => {
        const compName = diagnostic.component_name || diagnostic.component_ui_name || 'Unknown Component';
        const faultType = diagnostic.fault_type || '';
        const key = `${compName}-${faultType}`;

        if (!acc[key]) {
          acc[key] = {
            component_name: compName,
            fault_type: faultType,
            health_status: diagnostic.health_status || componentHealthStatus || 'na',
            diagnostic_id: diagnostic.diagnostic_id || `${compName}-${faultType}`,
            observations: [],
          };
        }

        // Add observations from sources_info
        const observations = (diagnostic.sources_info || [])
          .filter((source: any) => source.evidence_text && source.evidence_text.trim())
          .map((source: any) => ({
            text: source.evidence_text,
            source_type: source.source_type,
            health_status: source.health_status,
            resource_id: source.evidence_resource_id,
          }));

        acc[key].observations.push(...observations);

        return acc;
      }, {});

      return Object.values(componentGroups)
        .map((group: any) => ({
          fault_type: group.fault_type ? `${group.component_name} - ${group.fault_type}` : group.component_name,
          health_status: group.health_status,
          diagnostic_id: group.diagnostic_id,
          observations: group.observations,
        }))
        .filter((group: any) => group.observations.length > 0);
    }

    // If we have evidences_text, create a single group (single component mode)
    if (evidencesText && evidencesText.length > 0) {
      // Get the first diagnostic for health status and fault type
      const firstDiagnostic = diagnostics && diagnostics.length > 0 ? diagnostics[0] : null;
      // Use component health status if available, otherwise use diagnostic health status
      const healthStatus = componentHealthStatus || firstDiagnostic?.health_status || 'na';

      // Single component mode - use evidencesText directly
      return [
        {
          fault_type: firstDiagnostic?.fault_type || componentName,
          health_status: healthStatus,
          diagnostic_id: firstDiagnostic?.diagnostic_id || 'component-observations',
          observations: evidencesText.map((text: string, index: number) => ({
            text,
            source_type: 'Observation',
            health_status: healthStatus,
            resource_id: null,
          })),
        },
      ];
    }

    if (!diagnostics || diagnostics.length === 0) return [];

    // Extract from diagnostics.sources_info
    return diagnostics
      .map((diagnostic: any) => {
        const observations = (diagnostic.sources_info || [])
          .filter((source: any) => source.evidence_text && source.evidence_text.trim())
          .map((source: any) => ({
            text: source.evidence_text,
            source_type: source.source_type,
            health_status: source.health_status,
            resource_id: source.evidence_resource_id,
          }));

        // In Asset Overview mode, prepend component name to fault_type
        const faultType =
          isAssetOverview && diagnostic.component_name
            ? `${diagnostic.component_name} - ${diagnostic.fault_type || ''}`
            : diagnostic.fault_type || componentName;

        return {
          fault_type: faultType,
          health_status: diagnostic.health_status,
          diagnostic_id: diagnostic.diagnostic_id,
          observations,
        };
      })
      .filter((group: FaultGroup) => group.observations.length > 0);
  }, [diagnostics, evidencesText, componentName, componentHealthStatus, isAssetOverview]);

  // Filter fault groups and observations based on search term
  const filteredFaultGroups = useMemo(() => {
    if (!searchTerm.trim()) {
      return faultGroups;
    }

    const lowerSearchTerm = searchTerm.toLowerCase();
    return faultGroups
      .map((group) => ({
        ...group,
        observations: group.observations.filter(
          (obs: ObservationItem) =>
            obs.text.toLowerCase().includes(lowerSearchTerm) ||
            obs.source_type.toLowerCase().includes(lowerSearchTerm) ||
            group.fault_type.toLowerCase().includes(lowerSearchTerm)
        ),
      }))
      .filter((group) => group.observations.length > 0);
  }, [faultGroups, searchTerm]);

  // Transform actions into groups similar to observations
  const actionGroups = useMemo(() => {
    if (actionsDescriptions && actionsDescriptions.length > 0) {
      const firstDiagnostic = diagnostics && diagnostics.length > 0 ? diagnostics[0] : null;
      const healthStatus = componentHealthStatus || firstDiagnostic?.health_status || 'na';

      // If Asset Overview mode, group by component from diagnostics
      if (isAssetOverview && diagnostics && diagnostics.length > 0) {
        // Group diagnostics by component
        const componentGroups = diagnostics.reduce((acc: any, diagnostic: any) => {
          const compName = diagnostic.component_name || diagnostic.component_ui_name || 'Unknown Component';
          const faultType = diagnostic.fault_type || '';
          const key = `${compName}-${faultType}`;

          if (!acc[key]) {
            acc[key] = {
              component_name: compName,
              fault_type: faultType,
              health_status: diagnostic.health_status || healthStatus,
              diagnostic_id: diagnostic.diagnostic_id || `${compName}-${faultType}-actions`,
              observations: [],
            };
          }

          // Add actions from diagnostic
          // Note: Actions are typically per diagnostic, not per source
          // We'll need to match actions to their respective components

          return acc;
        }, {});

        // Since actions are aggregated, we'll create groups based on components
        return Object.values(componentGroups)
          .map((group: any) => ({
            fault_type: group.fault_type ? `${group.component_name} - ${group.fault_type}` : group.component_name,
            health_status: group.health_status,
            diagnostic_id: group.diagnostic_id,
            observations: [], // Actions will need to be mapped separately if available per component
          }))
          .filter((group: any) => group.observations.length > 0);
      }

      // Single component mode
      return [
        {
          fault_type: firstDiagnostic?.fault_type || componentName,
          health_status: healthStatus,
          diagnostic_id: firstDiagnostic?.diagnostic_id || 'component-actions',
          observations: actionsDescriptions.map((text: string) => ({
            text,
            source_type: 'Action',
            health_status: healthStatus,
            resource_id: null,
          })),
        },
      ];
    }
    return [];
  }, [actionsDescriptions, diagnostics, componentName, componentHealthStatus, isAssetOverview]);

  // Filter actions based on search term
  const filteredActionGroups = useMemo(() => {
    if (!searchTerm.trim()) {
      return actionGroups;
    }

    const lowerSearchTerm = searchTerm.toLowerCase();
    return actionGroups
      .map((group) => ({
        ...group,
        observations: group.observations.filter(
          (obs: ObservationItem) =>
            obs.text.toLowerCase().includes(lowerSearchTerm) || group.fault_type.toLowerCase().includes(lowerSearchTerm)
        ),
      }))
      .filter((group) => group.observations.length > 0);
  }, [actionGroups, searchTerm]);

  // Calculate total observations count
  const totalObservations = useMemo(() => {
    return faultGroups.reduce((sum, group) => sum + group.observations.length, 0);
  }, [faultGroups]);

  const filteredTotalObservations = useMemo(() => {
    return filteredFaultGroups.reduce((sum, group) => sum + group.observations.length, 0);
  }, [filteredFaultGroups]);

  // Calculate total actions count
  const totalActions = useMemo(() => {
    return actionGroups.reduce((sum, group) => sum + group.observations.length, 0);
  }, [actionGroups]);

  const filteredTotalActions = useMemo(() => {
    return filteredActionGroups.reduce((sum, group) => sum + group.observations.length, 0);
  }, [filteredActionGroups]);

  // Auto-expand accordion items when searching
  React.useEffect(() => {
    if (searchTerm.trim()) {
      // Expand all items that have matching results
      const itemsToExpand = filteredFaultGroups.map((group) => group.diagnostic_id);
      setExpandedItems(itemsToExpand);
    } else {
      // When search is cleared, expand only the first item
      setExpandedItems(filteredFaultGroups[0]?.diagnostic_id ? [filteredFaultGroups[0].diagnostic_id] : []);
    }
  }, [searchTerm, filteredFaultGroups]);

  // Sync activeTab with initialTab when it changes
  React.useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  const handleOpenChange = (open: boolean) => {
    // Reset search when drawer closes
    if (!open) {
      setSearchTerm('');
    }
    onOpenChange?.(open);
  };

  return (
    <BaseDrawer
      direction="right"
      onOpenChange={handleOpenChange}
      customContent={
        <DrawerContent
          className="fixed bottom-0 right-0 mt-0 h-full w-full max-w-md rounded-none border-l shadow-2xl"
          aria-describedby="observations-drawer-description"
        >
          {/* Header */}
          <DrawerHeader className="px-6 py-4 space-y-0">
            <div className="flex items-center justify-between mb-4">
              <DrawerTitle className="text-lg font-semibold text-left">{componentName}</DrawerTitle>
              <DrawerClose asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <X className="h-4 w-4" />
                  <span className="sr-only">Close</span>
                </Button>
              </DrawerClose>
            </div>

            {/* Tabs */}
            {(visibilityConfig.observations || visibilityConfig.actions) && (
              <div className="border-b -mx-6 px-6">
                <div className="flex gap-6">
                  {visibilityConfig.observations && (
                    <button
                      onClick={() => setActiveTab('observations')}
                      className={cn(
                        'pb-3 px-1 text-sm font-medium border-b-2 transition-colors',
                        activeTab === 'observations' && visibilityConfig.actions
                          ? 'border-primary text-foreground'
                          : 'border-transparent text-muted-foreground hover:text-foreground'
                      )}
                    >
                      Observations {totalObservations > 0 && `(${totalObservations})`}
                    </button>
                  )}
                  {visibilityConfig.actions && (
                    <button
                      onClick={() => setActiveTab('actions')}
                      disabled={totalActions === 0}
                      className={cn(
                        'pb-3 px-1 text-sm font-medium border-b-2 transition-colors',
                        activeTab === 'actions'
                          ? 'border-primary text-foreground'
                          : 'border-transparent text-muted-foreground hover:text-foreground',
                        totalActions === 0 && 'opacity-50 cursor-not-allowed'
                      )}
                    >
                      Actions {totalActions > 0 && `(${totalActions})`}
                    </button>
                  )}
                </div>
              </div>
            )}
            <p id="observations-drawer-description" className="sr-only">
              {activeTab === 'observations' ? 'Observations' : 'Actions'}
            </p>
          </DrawerHeader>

          {/* Search Panel */}
          <div className="px-6 py-4 border-b bg-muted/30">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder={
                  activeTab === 'observations' ? 'Search observations, faults, or sources...' : 'Search actions...'
                }
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4"
              />
              {searchTerm && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                  onClick={() => setSearchTerm('')}
                >
                  <X className="h-3 w-3" />
                  <span className="sr-only">Clear search</span>
                </Button>
              )}
            </div>
            {searchTerm && (
              <p className="text-xs text-muted-foreground mt-2">
                {activeTab === 'observations'
                  ? `${filteredTotalObservations} of ${totalObservations} observation${
                      totalObservations !== 1 ? 's' : ''
                    }`
                  : `${filteredTotalActions} of ${totalActions} action${totalActions !== 1 ? 's' : ''}`}
              </p>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {visibilityConfig.observations && activeTab === 'observations' && (
              <>
                {filteredFaultGroups.length > 0 ? (
                  <Accordion
                    type="multiple"
                    value={expandedItems}
                    onValueChange={setExpandedItems}
                    className="space-y-4"
                  >
                    {filteredFaultGroups.map((faultGroup) => (
                      <AccordionItem
                        key={faultGroup.diagnostic_id}
                        value={faultGroup.diagnostic_id}
                        className="border rounded-lg overflow-hidden"
                      >
                        <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-accent/5">
                          <div className="flex items-start justify-between w-full pr-2 gap-2">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <div className="flex-shrink-0">
                                <Lightbulb className="h-5 w-5 text-amber-500" />
                              </div>
                              <div className="flex-1 min-w-0 text-left">
                                <div className="font-semibold text-sm break-words">
                                  <Highlight text={faultGroup.fault_type} searchTerm={searchTerm} />
                                </div>
                                <div className="text-xs text-muted-foreground mt-0.5">
                                  {faultGroup.observations.length} observation
                                  {faultGroup.observations.length !== 1 ? 's' : ''}
                                </div>
                              </div>
                            </div>
                            <div className="flex-shrink-0 text-white">
                              <span
                                className={cn(
                                  'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium capitalize whitespace-nowrap',
                                  healthStatusColors[faultGroup.health_status] || healthStatusColors.na
                                )}
                              >
                                {faultGroup.health_status === 'na' ? 'N/A' : faultGroup.health_status}
                              </span>
                            </div>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="px-4 pb-4 pt-2">
                          <div className="space-y-3">
                            {faultGroup.observations.map((observation: ObservationItem, obsIndex: number) => (
                              <div
                                key={obsIndex}
                                className={cn(
                                  'flex gap-3 p-3 rounded-lg border bg-card/50 hover:bg-accent/5 transition-colors border-l-4 '
                                  // ,`border-l-4 ${healthStatusBorderColors[observation.health_status] || healthStatusBorderColors.na}`
                                )}
                              >
                                <div className="flex-shrink-0 mt-0.5 text-muted-foreground">
                                  {/* {getSourceIcon(observation.source_type)} */}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">
                                    <Highlight text={observation.text} searchTerm={searchTerm} />
                                  </p>
                                  {observation.source_type && observation.source_type !== 'Observation' && (
                                    <div className="flex items-center gap-2 mt-2">
                                      <span className="text-xs text-muted-foreground">
                                        <Highlight text={observation.source_type} searchTerm={searchTerm} />
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                ) : searchTerm ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Search className="h-12 w-12 text-muted-foreground/50 mb-4" />
                    <p className="text-sm text-muted-foreground">No observations found matching "{searchTerm}"</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Lightbulb className="h-12 w-12 text-muted-foreground/50 mb-4" />
                    <p className="text-sm text-muted-foreground">No observations available</p>
                  </div>
                )}
              </>
            )}

            {visibilityConfig.actions && activeTab === 'actions' && (
              <>
                {filteredActionGroups.length > 0 ? (
                  <Accordion
                    type="multiple"
                    value={expandedItems}
                    onValueChange={setExpandedItems}
                    className="space-y-4"
                  >
                    {filteredActionGroups.map((actionGroup) => (
                      <AccordionItem
                        key={actionGroup.diagnostic_id}
                        value={actionGroup.diagnostic_id}
                        className="border rounded-lg overflow-hidden"
                      >
                        <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-accent/5">
                          <div className="flex items-start justify-between w-full pr-2 gap-2">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <div className="flex-shrink-0">
                                <CheckSquare className="h-5 w-5 text-blue-500" />
                              </div>
                              <div className="flex-1 min-w-0 text-left">
                                <div className="font-semibold text-sm break-words">
                                  <Highlight text={actionGroup.fault_type} searchTerm={searchTerm} />
                                </div>
                                <div className="text-xs text-muted-foreground mt-0.5">
                                  {actionGroup.observations.length} action
                                  {actionGroup.observations.length !== 1 ? 's' : ''}
                                </div>
                              </div>
                            </div>
                            <div className="flex-shrink-0">
                              <span
                                className={cn(
                                  'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium capitalize whitespace-nowrap',
                                  healthStatusColors[actionGroup.health_status] || healthStatusColors.na
                                )}
                              >
                                {actionGroup.health_status === 'na' ? 'N/A' : actionGroup.health_status}
                              </span>
                            </div>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="px-4 pb-4 pt-2">
                          <div className="space-y-3">
                            {actionGroup.observations.map((action: ObservationItem, actIndex: number) => (
                              <div
                                key={actIndex}
                                className={cn(
                                  'flex gap-3 p-3 rounded-lg border bg-card/50 hover:bg-accent/5 transition-colors border-l-4',
                                  healthStatusBorderColors[action.health_status] || healthStatusBorderColors.na
                                )}
                              >
                                <div className="flex-shrink-0 mt-0.5 text-muted-foreground">
                                  <CheckSquare className="h-4 w-4" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">
                                    <Highlight text={action.text} searchTerm={searchTerm} />
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                ) : searchTerm ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Search className="h-12 w-12 text-muted-foreground/50 mb-4" />
                    <p className="text-sm text-muted-foreground">No actions found matching "{searchTerm}"</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <CheckSquare className="h-12 w-12 text-muted-foreground/50 mb-4" />
                    <p className="text-sm text-muted-foreground">No actions available</p>
                  </div>
                )}
              </>
            )}
          </div>
        </DrawerContent>
      }
    >
      {children}
    </BaseDrawer>
  );
};
